import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { FiGrid, FiMail, FiLock, FiEye, FiEyeOff, FiBriefcase, FiUser, FiArrowRight, FiPhone, FiShield } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import axios from 'axios'

const PLAN_NAMES = { pro: 'Pro', avanzado: 'Avanzado' }

const Registro = () => {
  const navigate = useNavigate()
  const { setAuthData } = useAuth()
  const [searchParams] = useSearchParams()

  const planFromUrl = searchParams.get('plan')
  const selectedPlan = PLAN_NAMES[planFromUrl] ? planFromUrl : 'pro'

  const [formData, setFormData] = useState({
    nombre_empresa: '',
    nombre_admin: '',
    telefono: '',
    email: '',
    password: '',
    password_confirm: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1) // 1: empresa, 2: credenciales, 3: verificación

  // Consentimiento RGPD (privacidad obligatorio, marketing opcional)
  const [consentPrivacidad, setConsentPrivacidad] = useState(false)
  const [consentMarketing, setConsentMarketing] = useState(false)

  // OTP state
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
  const [expiresAt, setExpiresAt] = useState(null)
  const [countdown, setCountdown] = useState(0)
  const [resendCooldown, setResendCooldown] = useState(0)
  const otpRefs = useRef([])

  // Countdown del código
  useEffect(() => {
    if (!expiresAt) return
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000))
      setCountdown(remaining)
      if (remaining === 0) clearInterval(timer)
    }, 1000)
    return () => clearInterval(timer)
  }, [expiresAt])

  // Cooldown del reenvío
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setInterval(() => setResendCooldown(prev => Math.max(0, prev - 1)), 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleStep1 = (e) => {
    e.preventDefault()
    if (!formData.nombre_empresa) {
      toast.error('El nombre de empresa es obligatorio')
      return
    }
    setStep(2)
  }

  const handleStep2 = async (e) => {
    e.preventDefault()

    if (formData.password !== formData.password_confirm) {
      toast.error('Las contraseñas no coinciden')
      return
    }
    if (formData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    if (!consentPrivacidad) {
      toast.error('Debes aceptar la política de privacidad para continuar')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post('/api/registro/send-verification', {
        nombre_empresa: formData.nombre_empresa,
        nombre_admin: formData.nombre_admin || formData.nombre_empresa,
        email: formData.email,
        password: formData.password,
        telefono: formData.telefono || null,
        plan: selectedPlan
      })

      setExpiresAt(response.data.expiresAt)
      setResendCooldown(60)
      setOtpCode(['', '', '', '', '', ''])
      setStep(3)
      toast.success('Código de verificación enviado a tu email')
      window.gtag?.('event', 'registro_iniciado', { plan: selectedPlan })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar el código de verificación')
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return
    const newCode = [...otpCode]
    newCode[index] = value
    setOtpCode(newCode)

    // Auto-focus siguiente
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setOtpCode(pasted.split(''))
      otpRefs.current[5]?.focus()
      e.preventDefault()
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    const code = otpCode.join('')
    if (code.length !== 6) {
      toast.error('Introduce el código completo de 6 dígitos')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post('/api/registro/verify-and-create', {
        nombre_empresa: formData.nombre_empresa,
        nombre_admin: formData.nombre_admin || formData.nombre_empresa,
        email: formData.email,
        password: formData.password,
        telefono: formData.telefono || null,
        plan: selectedPlan,
        code,
        consent_privacidad: consentPrivacidad,
        consent_marketing: consentMarketing
      })

      const { token, empleado, empresa } = response.data

      if (setAuthData) {
        setAuthData(token, empleado, empresa)
      } else {
        localStorage.setItem('token', token)
      }

      sessionStorage.setItem('login_timestamp', Date.now().toString())
      toast.success('¡Empresa registrada! Bienvenido a PresenzIA')
      window.gtag?.('event', 'trial_activado', { plan: selectedPlan, empresa: formData.nombre_empresa })
      navigate('/', { replace: true })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al verificar el código')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    setLoading(true)
    try {
      const response = await axios.post('/api/registro/send-verification', {
        nombre_empresa: formData.nombre_empresa,
        nombre_admin: formData.nombre_admin || formData.nombre_empresa,
        email: formData.email,
        password: formData.password,
        telefono: formData.telefono || null,
        plan: selectedPlan
      })
      setExpiresAt(response.data.expiresAt)
      setResendCooldown(60)
      setOtpCode(['', '', '', '', '', ''])
      toast.success('Nuevo código enviado')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al reenviar código')
    } finally {
      setLoading(false)
    }
  }

  const formatCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg mb-4">
            <FiGrid className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Crear cuenta</h2>
          <p className="mt-2 text-sm text-gray-600">
            Registra tu empresa y empieza con 14 días gratis
          </p>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold ${
            selectedPlan === 'avanzado'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-blue-100 text-blue-700'
          }`}>
            Plan {PLAN_NAMES[selectedPlan]} - 14 días gratis
          </span>
        </div>

        {/* Indicador de pasos */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-1.5 rounded-full transition-all ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1.5 rounded-full transition-all ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1.5 rounded-full transition-all ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
        </div>

        {/* Paso 1: Datos de empresa */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="card-glass space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Datos de la empresa</h3>

            <div>
              <label htmlFor="nombre_empresa" className="label">Nombre de la empresa</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiBriefcase className="h-5 w-5 text-gray-400" />
                </div>
                <input id="nombre_empresa" name="nombre_empresa" type="text" required
                  value={formData.nombre_empresa} onChange={handleChange}
                  className="input-glass pl-12" placeholder="Mi Empresa S.L." />
              </div>
            </div>

            <div>
              <label htmlFor="nombre_admin" className="label">Tu nombre (administrador)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-gray-400" />
                </div>
                <input id="nombre_admin" name="nombre_admin" type="text"
                  value={formData.nombre_admin} onChange={handleChange}
                  className="input-glass pl-12" placeholder="Juan García" />
              </div>
            </div>

            <div>
              <label htmlFor="telefono" className="label">
                Teléfono <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiPhone className="h-5 w-5 text-gray-400" />
                </div>
                <input id="telefono" name="telefono" type="tel"
                  value={formData.telefono} onChange={handleChange}
                  className="input-glass pl-12" placeholder="600 123 456" />
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-ripple w-full flex items-center justify-center gap-2">
              Siguiente <FiArrowRight className="h-4 w-4" />
            </button>

            <p className="text-sm text-center text-gray-500 mt-4">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">Inicia sesión</Link>
            </p>
          </form>
        )}

        {/* Paso 2: Credenciales */}
        {step === 2 && (
          <form onSubmit={handleStep2} className="card-glass space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-blue-600 hover:text-blue-800">
                &larr; Volver
              </button>
              <h3 className="text-lg font-semibold text-gray-800">Tu cuenta de acceso</h3>
            </div>

            <div className="bg-blue-50 rounded-xl px-4 py-2 text-sm text-blue-700">
              Empresa: <strong>{formData.nombre_empresa}</strong>
            </div>

            <div>
              <label htmlFor="email" className="label">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input id="email" name="email" type="email" autoComplete="email" required
                  value={formData.email} onChange={handleChange}
                  className="input-glass pl-12" placeholder="tu@empresa.com" />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">Contraseña</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input id="password" name="password" type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password" required
                  value={formData.password} onChange={handleChange}
                  className="input-glass pl-12 pr-12" placeholder="Mínimo 8 caracteres" />
                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="password_confirm" className="label">Confirmar contraseña</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input id="password_confirm" name="password_confirm"
                  type={showPassword ? 'text' : 'password'} autoComplete="new-password" required
                  value={formData.password_confirm} onChange={handleChange}
                  className="input-glass pl-12" placeholder="Repite la contraseña" />
              </div>
            </div>

            {/* Consentimiento RGPD */}
            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentPrivacidad}
                  onChange={(e) => setConsentPrivacidad(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  He leído y acepto la{' '}
                  <Link to="/privacidad" target="_blank" className="text-blue-600 hover:text-blue-800 underline">
                    Política de privacidad
                  </Link>{' '}
                  y el{' '}
                  <Link to="/aviso-legal" target="_blank" className="text-blue-600 hover:text-blue-800 underline">
                    Aviso legal
                  </Link>. <span className="text-red-500">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  Quiero recibir novedades del producto y consejos sobre control horario por email
                  (opcional, puedes darte de baja en cualquier momento).
                </span>
              </label>
            </div>

            <button type="submit" disabled={loading}
              className="btn btn-primary btn-ripple w-full">
              {loading ? 'Enviando código...' : 'Verificar email'}
            </button>
          </form>
        )}

        {/* Paso 3: Verificación OTP */}
        {step === 3 && (
          <form onSubmit={handleVerify} className="card-glass space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <button type="button" onClick={() => setStep(2)} className="text-sm text-blue-600 hover:text-blue-800">
                &larr; Volver
              </button>
              <h3 className="text-lg font-semibold text-gray-800">Verifica tu email</h3>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-2xl mb-3">
                <FiShield className="h-7 w-7 text-blue-600" />
              </div>
              <p className="text-sm text-gray-600">
                Hemos enviado un código de 6 dígitos a
              </p>
              <p className="text-sm font-bold text-gray-900 mt-1">{formData.email}</p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {otpCode.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {/* Countdown */}
            {countdown > 0 && (
              <p className="text-center text-xs text-gray-400">
                Código válido durante <span className="font-mono font-bold">{formatCountdown(countdown)}</span>
              </p>
            )}
            {countdown === 0 && expiresAt && (
              <p className="text-center text-xs text-red-500 font-medium">
                Código expirado. Solicita uno nuevo.
              </p>
            )}

            <button type="submit" disabled={loading || otpCode.join('').length !== 6}
              className="btn btn-primary btn-ripple w-full">
              {loading ? 'Verificando...' : 'Crear cuenta'}
            </button>

            {/* Reenviar */}
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-xs text-gray-400">
                  Reenviar código en {resendCooldown}s
                </p>
              ) : (
                <button type="button" onClick={handleResend} disabled={loading}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                  Reenviar código
                </button>
              )}
            </div>

            <p className="text-xs text-center text-gray-500">
              Al verificar el código completas tu registro conforme a la{' '}
              <Link to="/privacidad" target="_blank" className="text-blue-600 hover:text-blue-800 underline">
                Política de privacidad
              </Link>{' '}
              que ya aceptaste.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

export default Registro
